import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import ScrollableWrapper from "../common/ScrollableWrapper";
import SearchBar from "../common/SearchBar";
import { getProviderLabPanels } from "../../../../common/network/Api";
import { getProviderLabel } from "../../store/helper";
import {
  RootStateServiceLogicBuilder,
  serviceLogicBuilderActions,
} from "../../store/service-logic-builder-slice";
import {
  DEFAULT_PROVIDER,
  getPanelCost,
  normalizeProviderPanel,
} from "./SavedPanelsScreen";
import {
  isPanelOrderable,
  resolvePanelLabStatus,
} from "../../utils/labPanelStatus";

type LabTab = "custom" | "provider";

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (panel: any) => void;
  // Panel ids already chosen in other rows — hidden so the same panel can't be
  // selected twice.
  excludeIds?: string[];
};

const SelectLabPanelModal = ({
  open,
  onClose,
  onSelect,
  excludeIds = [],
}: Props) => {
  const theme: Theme = useTheme();
  const dispatch = useDispatch();

  // The modal always opens on the first tab (the provider catalog) regardless
  // of where the user left the Lab Builder, so this is local state rather than
  // the persisted Redux tab.
  const [activeTab, setActiveTab] = useState<LabTab>("provider");
  const [searchFilter, setSearchFilter] = useState("");

  const savedLabPanels = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.savedLabPanels
  );
  // `null` means "not loaded yet" — also our loading signal.
  const providerPanels = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.providerLabPanels[DEFAULT_PROVIDER] ?? null
  );

  const isProviderTab = activeTab === "provider";
  const providerLoading = isProviderTab && providerPanels === null;

  // Reset tab/search each time the modal opens.
  useEffect(() => {
    if (open) {
      setActiveTab("provider");
      setSearchFilter("");
    }
  }, [open]);

  // Lazily load the provider's predefined panels the first time the Default tab
  // is opened — but only if they aren't already cached in Redux.
  useEffect(() => {
    if (!open || !isProviderTab || providerPanels !== null) return;

    let cancelled = false;
    getProviderLabPanels(DEFAULT_PROVIDER)
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray(response)
          ? response.map(normalizeProviderPanel)
          : [];
        dispatch(
          serviceLogicBuilderActions.setProviderLabPanels({
            provider: DEFAULT_PROVIDER,
            panels: list,
          })
        );
      })
      .catch(() => {
        if (cancelled) return;
        dispatch(
          serviceLogicBuilderActions.setProviderLabPanels({
            provider: DEFAULT_PROVIDER,
            panels: [],
          })
        );
      });

    return () => {
      cancelled = true;
    };
  }, [open, isProviderTab, providerPanels, dispatch]);

  const sourcePanels = isProviderTab ? providerPanels ?? [] : savedLabPanels;

  const filteredPanels = useMemo(() => {
    const query = searchFilter.trim().toLowerCase();
    const excluded = new Set(excludeIds);
    const list = sourcePanels.filter((panel: any) => {
      if (excluded.has(panel.id?.toString())) return false;
      if (!query) return true;
      return (panel.name || "").toLowerCase().includes(query);
    });
    // Custom tab: the selectable (lab-active) panels come first, the faded ones
    // sink below them — the sort is stable, so each group keeps its own order.
    // The provider catalog carries no statuses, so it's left as the API sent it.
    if (isProviderTab) return list;
    return [...list].sort(
      (a: any, b: any) =>
        Number(isPanelOrderable(b)) - Number(isPanelOrderable(a))
    );
  }, [sourcePanels, searchFilter, excludeIds, isProviderTab]);

  return (
    <Modal open={open} onClose={onClose}>
      <div style={styles.modal(theme)}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.title(theme)}>Select Lab Panel</div>
          <div
            style={styles.closeButton}
            onClick={onClose}
            role="button"
            aria-label="Close"
          >
            <XIconSvg />
          </div>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            type="button"
            style={styles.tab(theme, isProviderTab)}
            onClick={() => setActiveTab("provider")}
          >
            Quest default
          </button>
          <button
            type="button"
            style={styles.tab(theme, !isProviderTab)}
            onClick={() => setActiveTab("custom")}
          >
            Custom
          </button>
        </div>

        {/* Search */}
        <SearchBar
          searchFilter={searchFilter}
          setSearchFilter={setSearchFilter}
          placeholder="Search"
        />

        {/* Panel list */}
        <div style={styles.listWrapper}>
          <ScrollableWrapper>
            <div style={styles.list}>
              {providerLoading ? (
                <div style={styles.emptyMessage(theme)}>Loading panels…</div>
              ) : filteredPanels.length === 0 ? (
                <div style={styles.emptyMessage(theme)}>
                  No panels found matching your search
                </div>
              ) : (
                filteredPanels.map((panel: any) => {
                  // Custom panels the lab hasn't activated can't be ordered:
                  // still listed, but faded and inert.
                  const orderable = isPanelOrderable(panel);
                  // Null for provider catalog panels, which have no lab status.
                  const labStatus = resolvePanelLabStatus(panel);
                  return (
                    <div
                      key={panel.id}
                      style={styles.card(theme, orderable)}
                      onClick={
                        orderable
                          ? () => {
                              onSelect(panel);
                              onClose();
                            }
                          : undefined
                      }
                    >
                      <div style={styles.cardInfo}>
                        <div style={styles.panelName(theme)}>{panel.name}</div>
                        <div style={styles.metaRow}>
                          <span style={styles.metaText(theme)}>
                            {`${panel.items?.length || 0} biomarkers`}
                          </span>
                          {getProviderLabel(panel.provider) && (
                            <>
                              <span style={styles.dot(theme)} />
                              <span style={styles.metaText(theme)}>
                                {getProviderLabel(panel.provider)}
                              </span>
                            </>
                          )}
                          {labStatus && (
                            <>
                              <span style={styles.dot(theme)} />
                              <span style={styles.metaText(theme)}>
                                {labStatus.label}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div style={styles.price(theme)}>
                        {`$${getPanelCost(panel)}`}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollableWrapper>
        </div>
      </div>
    </Modal>
  );
};

export default SelectLabPanelModal;

const styles = {
  modal: (theme: Theme) => ({
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "600px",
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "calc(100vh - 64px)",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderRadius: "8px",
    padding: "24px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    outline: "none",
  }),
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  closeButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px",
    borderRadius: "4px",
    cursor: "pointer",
  },
  tabs: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
  },
  tab: (theme: Theme, active: boolean) => ({
    background: "none",
    border: "none",
    padding: "4px 0",
    cursor: "pointer",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.6",
    color: active
      ? theme.colors_ds.NEUTRAL_TEXT_DEFAULT
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
    borderBottom: active
      ? `2px solid ${theme.colors_ds.PRIMARY_TEXT_MAIN}`
      : "2px solid transparent",
  }),
  listWrapper: {
    display: "flex",
    flexDirection: "column" as "column",
    // Fixed height so the modal keeps the same dimensions regardless of how
    // many panels a tab has (Custom vs Default) — the list scrolls internally.
    height: "420px",
    marginRight: "-10px",
  },
  list: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
  },
  card: (theme: Theme, orderable: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "8px 12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    cursor: orderable ? "pointer" : "default",
    opacity: orderable ? 1 : 0.5,
  }),
  cardInfo: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    flex: 1,
    minWidth: 0,
  },
  panelName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  metaText: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  dot: (theme: Theme) => ({
    width: "4px",
    height: "4px",
    borderRadius: "50%",
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
    flexShrink: 0,
  }),
  price: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    flexShrink: 0,
  }),
  emptyMessage: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    padding: "16px 0",
    textAlign: "center" as "center",
  }),
};
